import isNumber from 'lodash-es/isNumber';
import {
    EditableDisableAction,
    EDITABLE_DISABLE,
    PostDetailCommentCreateFailureAction,
    PostDetailCommentCreateRequestAction,
    PostDetailCommentCreateSuccessAction,
    PostDetailFetchFailureAction,
    PostDetailFetchRequestAction,
    PostDetailFetchSuccessAction,
    PostDetailToggleCommentHighlightAction,
    POST_DETAIL_COMMENT_CREATE_FAILURE,
    POST_DETAIL_COMMENT_CREATE_REQUEST,
    POST_DETAIL_COMMENT_CREATE_SUCCESS,
    POST_DETAIL_FETCH_FAILURE,
    POST_DETAIL_FETCH_REQUEST,
    POST_DETAIL_FETCH_SUCCESS,
    POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT,
} from 'mk2/apps/forum/containers/PostDetail/PostDetail.actions';
import {
    initialPostDetailPageState,
    initialPostDetailState,
    initialPostDetailVirtualPageState,
    ITEMS_PER_PAGE,
} from 'mk2/apps/forum/containers/PostDetail/PostDetail.constants';
import { tupdate } from 'mk2/helpers/types';
import { errorMessage } from 'mk2/reducers/errorMessage';
import { loadingState, LoadingState } from 'mk2/reducers/loadingState';
import { Reducer } from 'redux';

export interface PostDetailPageState {
    commentIds: number[];
    loadingState: LoadingState;
    errorMessage: string;
}

export interface PostDetailVirtualPageState {
    loadingState: LoadingState;
    errorMessage: string;
}

export interface PostDetailState {
    pages: {[pageNum: number]: PostDetailPageState};
    virtualPages: {[pageName: string]: PostDetailVirtualPageState};  // pageName = 'first'|'last'...
    numberOfPages: number;
    contestId: number;
    contestAlreadyParticipated: boolean;
    commentsWithComplaintIds: number[];
    commentToBeHighlighted: number | 'last';
    similarPostIds: number[];
    recentPostIds: number[];
    backlinksBoxId: number;
    myAnonymousCommentsIds: number[];
    counselling: number;
    brandDaysCampaignId: number;
    brandDaysPostsIds: number[];
    isMyAnonymousPost: boolean;

    eshopProductsHeaderIds: number[];
    eshopProductsIds: number[];
    eshopProductsFooterIds: number[];

    eshopProductsOfEshopNodes: Record<string, number[]>;

    promoReviewArticlesHeaderIds: number[];
    promoReviewArticlesMiddleIds: number[];
    promoReviewArticlesFooterIds: number[];
}

declare type PostDetailReducerAction =
    PostDetailFetchRequestAction
    | PostDetailFetchSuccessAction
    | PostDetailFetchFailureAction
    | PostDetailCommentCreateRequestAction
    | PostDetailCommentCreateSuccessAction
    | PostDetailCommentCreateFailureAction
    | EditableDisableAction
    | PostDetailToggleCommentHighlightAction;

const PostDetailReducer: Reducer<PostDetailState> = (state: PostDetailState = initialPostDetailState, action: PostDetailReducerAction) => {
    switch (action.type) {
        case POST_DETAIL_FETCH_REQUEST:
        case POST_DETAIL_FETCH_FAILURE: {
            if (isNumber(action.pageRef)) {
                return tupdate(state, {
                    pages: tupdate(state.pages, {
                        [action.pageRef]: tupdate(state.pages[action.pageRef] || initialPostDetailPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            } else {
                return tupdate(state, {
                    virtualPages: tupdate(state.virtualPages, {
                        [action.pageRef]: tupdate(state.virtualPages[action.pageRef] || initialPostDetailVirtualPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            }
        }

        case POST_DETAIL_FETCH_SUCCESS:
            if (!isNumber(action.page)) {
                throw new Error('Page must be a number');
            }

            return tupdate(state, {
                numberOfPages: action.numberOfPages,
                contestId: action.response.result.contest,
                contestAlreadyParticipated: action.contestAlreadyParticipated,
                // update state of page under both - virtual name and real index
                virtualPages: isNumber(action.pageRef) ? state.virtualPages : tupdate(state.virtualPages, {
                    [action.pageRef]: tupdate(state.virtualPages[action.pageRef] || initialPostDetailVirtualPageState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
                pages: tupdate(state.pages, {
                    [action.page]: tupdate(state.pages[action.page] || initialPostDetailPageState, {
                        commentIds: action.response.result.comments,
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
                commentsWithComplaintIds: action.response.result.commentsWithComplaint,
                similarPostIds: action.response.result.similarPosts,
                recentPostIds: action.response.result.recentPosts,
                backlinksBoxId: action.response.result.backlinksBox,
                myAnonymousCommentsIds: action.myAnonymousComments,
                counselling: action.response.result.counselling,
                brandDaysCampaignId: action.response.result.brandDays.campaign || initialPostDetailState.brandDaysCampaignId,
                brandDaysPostsIds: action.response.result.brandDays.posts || initialPostDetailState.brandDaysPostsIds,
                isMyAnonymousPost: action.isMyAnonymousPost,

                eshopProductsHeaderIds: action.response.result.eshopProductsHeader,
                eshopProductsIds: action.response.result.eshopProducts,
                eshopProductsFooterIds: action.response.result.eshopProductsFooter,

                eshopProductsOfEshopNodes: action.response.result.eshopProductsOfEshopNodes,

                promoReviewArticlesHeaderIds: action.response.result.promoReviewArticlesHeader,
                promoReviewArticlesMiddleIds: action.response.result.promoReviewArticlesMiddle,
                promoReviewArticlesFooterIds: action.response.result.promoReviewArticlesFooter,
            });

        case EDITABLE_DISABLE:
            return tupdate(state, {
                [action.page]: tupdate(state.pages[action.page], {
                    commentIds: [...state.pages[action.page].commentIds],
                }),
            });

        case POST_DETAIL_COMMENT_CREATE_SUCCESS:
            if (!state.pages[state.numberOfPages]) {
                // If last page is not loaded - skip
                return tupdate(state, {
                    contestAlreadyParticipated: action.contestAlreadyParticipated,
                });
            }

            // Check last page length
            if (state.pages[state.numberOfPages].commentIds.length < ITEMS_PER_PAGE) {
                // Append to last page
                return tupdate(state, {
                    contestId: action.response.result.contest,
                    contestAlreadyParticipated: action.contestAlreadyParticipated,
                    myAnonymousCommentsIds: [...state.myAnonymousCommentsIds, action.response.result.myAnonymousComment],
                    pages: {
                        ...state.pages,
                        [state.numberOfPages]: {
                            ...state.pages[state.numberOfPages],
                            commentIds: [...state.pages[state.numberOfPages].commentIds, action.response.result.comment],
                        },
                    },
                });
            } else {
                // Create new page
                return tupdate(state, {
                    contestId: action.response.result.contest,
                    contestAlreadyParticipated: action.contestAlreadyParticipated,
                    pages: {
                        ...state.pages,
                        [state.numberOfPages + 1]: {
                            ...state.pages[state.numberOfPages + 1],
                            commentIds: [action.response.result.comment],
                        },
                    },
                    numberOfPages: state.numberOfPages + 1,
                });
            }

        case POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT:
            return tupdate(state, {
                commentToBeHighlighted: action.commentId,
            });

        default:
            return state;
    }
};

export interface PostsDetailState {
    stateBySlug: {
        [key: string]: PostDetailState;
    };
    userCanHavePregnancyNewsletter: boolean;
}

export const initialPostsDetailState: PostsDetailState = {
    stateBySlug: {},
    userCanHavePregnancyNewsletter: true,
};

declare type PostsDetailReducerAction =
    PostDetailFetchRequestAction
    | PostDetailFetchSuccessAction
    | PostDetailFetchFailureAction
    | PostDetailCommentCreateRequestAction
    | PostDetailCommentCreateSuccessAction
    | PostDetailCommentCreateFailureAction
    | PostDetailToggleCommentHighlightAction;

const postsDetailReducer: Reducer<PostsDetailState> = (state = initialPostsDetailState, action: PostsDetailReducerAction) => {
    let key;
    switch (action.type) {
        case POST_DETAIL_FETCH_REQUEST:
        case POST_DETAIL_FETCH_FAILURE: {
            key = `${action.topicSlug}/${action.postSlug}`;
            return tupdate(state, {
                stateBySlug: tupdate(state.stateBySlug, {
                    [key]: PostDetailReducer(state.stateBySlug[key], action),
                }),
            });
        }

        case POST_DETAIL_FETCH_SUCCESS: {
            key = `${action.topicSlug}/${action.postSlug}`;
            return tupdate(state, {
                stateBySlug: tupdate(state.stateBySlug, {
                    [key]: PostDetailReducer(state.stateBySlug[key], action),
                }),
                userCanHavePregnancyNewsletter: action.userCanHavePregnancyNewsletter,
            });
        }

        case POST_DETAIL_COMMENT_CREATE_REQUEST:
        case POST_DETAIL_COMMENT_CREATE_SUCCESS:
        case POST_DETAIL_COMMENT_CREATE_FAILURE:
        case POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT:
            key = action.key;
            return tupdate(state, {
                stateBySlug: tupdate(state.stateBySlug, {
                    [key]: PostDetailReducer(state.stateBySlug[key], action),
                }),
            });

        default:
            return state;
    }
};

export default postsDetailReducer;
